/** @format */

import {useState, useCallback, useMemo, useEffect} from 'react';
import type {ReactNode} from 'react';

import {SelectChangeEvent} from '@mui/material/Select';

import {
  YalperAnalyticAttributionModelEnum,
  AiKpiType,
  CampaignSelectionMode,
  AiMonitoringConfig,
} from 'graphql/generated/types';

import {validateBudgetInput} from '../../Form/helpers';

export interface MonitoringFormState {
  campaigns: string[];
  goals: string[];
  kpi: AiKpiType | '';
  kpiAmount: string;
  attributionModel: YalperAnalyticAttributionModelEnum;
  budget: string;
  campaignSelectionMode: CampaignSelectionMode;
  campaignNameMask: string;
}

const getEmptyState = (): MonitoringFormState => ({
  campaigns: [],
  goals: [],
  kpi: '',
  kpiAmount: '',
  attributionModel: 'LASTSIGN',
  budget: '',
  campaignSelectionMode: 'AUTO',
  campaignNameMask: '',
});

const configToFormState = (config: AiMonitoringConfig): MonitoringFormState => ({
  campaigns: config.campaignIds?.map(String) ?? [],
  goals: config.goals.map(g => String(g.id)),
  kpi: config.kpi,
  kpiAmount: config.kpiValue,
  attributionModel: config.attributionModel,
  budget: config.budget ?? '',
  campaignSelectionMode: config.campaignSelectionMode,
  campaignNameMask: config.campaignNameMask ?? '',
});

const arraysEqual = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((val, idx) => val === sortedB[idx]);
};

const isDeepEqual = (a: MonitoringFormState, b: MonitoringFormState): boolean => {
  return (
    arraysEqual(a.campaigns, b.campaigns) &&
    arraysEqual(a.goals, b.goals) &&
    a.kpi === b.kpi &&
    a.kpiAmount === b.kpiAmount &&
    a.attributionModel === b.attributionModel &&
    a.budget === b.budget &&
    a.campaignSelectionMode === b.campaignSelectionMode &&
    a.campaignNameMask === b.campaignNameMask
  );
};

export const isMonitoringFormValid = (state: MonitoringFormState): boolean => {
  const hasCampaigns =
    state.campaignSelectionMode === 'AUTO'
      ? state.campaignNameMask.trim() !== ''
      : state.campaigns.length > 0;

  return (
    hasCampaigns &&
    state.goals.length > 0 &&
    state.kpi !== '' &&
    state.kpiAmount !== '' &&
    state.attributionModel !== ('' as YalperAnalyticAttributionModelEnum)
  );
};

export const useMonitoringConfigForm = (config: AiMonitoringConfig | null | undefined) => {
  const initialFromConfig = config ? configToFormState(config) : getEmptyState();
  const [state, setState] = useState<MonitoringFormState>(initialFromConfig);
  const [initialState, setInitialState] = useState<MonitoringFormState>(initialFromConfig);

  useEffect(() => {
    const formState = config ? configToFormState(config) : getEmptyState();
    setState(formState);
    setInitialState(formState);
  }, [config]);

  const isDirty = useMemo(() => !isDeepEqual(state, initialState), [state, initialState]);
  const isValid = useMemo(() => isMonitoringFormValid(state), [state]);

  const handleCampaignChange = useCallback((campaignIds: string[]) => {
    setState(prev => ({...prev, campaigns: campaignIds}));
  }, []);

  const handleGoalChange = useCallback((goalIds: string[]) => {
    setState(prev => ({...prev, goals: goalIds}));
  }, []);

  const handleKpiChange = useCallback((event: SelectChangeEvent<unknown>, _child: ReactNode) => {
    setState(prev => ({...prev, kpi: event.target.value as AiKpiType}));
  }, []);

  const handleKpiAmountChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (validateBudgetInput(value)) {
      setState(prev => ({...prev, kpiAmount: value}));
    }
  }, []);

  const handleAttributionChange = useCallback(
    (event: SelectChangeEvent<unknown>, _child: ReactNode) => {
      setState(prev => ({
        ...prev,
        attributionModel: event.target.value as YalperAnalyticAttributionModelEnum,
      }));
    },
    [],
  );

  const handleBudgetChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (validateBudgetInput(value)) {
      setState(prev => ({...prev, budget: value}));
    }
  }, []);

  const handleCampaignSelectionModeChange = useCallback((mode: CampaignSelectionMode) => {
    setState(prev => ({
      ...prev,
      campaignSelectionMode: mode,
      campaigns: [],
      campaignNameMask: '',
    }));
  }, []);

  const handleCampaignNameMaskChange = useCallback((mask: string) => {
    setState(prev => ({...prev, campaignNameMask: mask}));
  }, []);

  const resetToInitial = useCallback(() => {
    setState(initialState);
  }, [initialState]);

  return {
    state,
    isDirty,
    isValid,
    handlers: {
      handleCampaignChange,
      handleGoalChange,
      handleKpiChange,
      handleKpiAmountChange,
      handleAttributionChange,
      handleBudgetChange,
      handleCampaignSelectionModeChange,
      handleCampaignNameMaskChange,
    },
    resetToInitial,
  };
};
